import { formatNumber } from '@components/numberFormat';
import { getPrevOrNextGLDocument } from './api';
import {
    GLAutoBookingModel,
    GLAccountModel,
    GeneralLedgerExtended,
    GlDocumentWithItems,
    SheetChange,
    StockHeaderEnums,
    GLAutoBookingGoodsManagement,
    GLAutoBookingRetailCalculation,
    GLAutoBookingInvoices,
    GLAutoBookingDocumentTypeNames,
    GLAutoBookingGoodsManagementValues,
    GLAutoBookingRetailCalculationValues,
    GLAutoBookingInvoiceValues,
    ClientTypeEnum,
    InvoiceType,
    GLAutoBookingInvoiceConclusion,
    GLAutoBookingInvoiceConclusionValues,
    GLAutoBookingDocumentNames,
    InvoiceTypeOfPayment,
    AutoBookingRule,
    AutoBookingRulesInterface,
    TypeAndClassModel,
    GLAutoBookingItemTypes,
    GLAutoBookingItemTypesTranslate,
    PosItemSubTypesTranslate,
} from '@common/modelDefinition';
import translate from '@data/translations';
import { TypeOfPaymentEnums } from '@common/enums';

export const findChildElements = (rootEl: any, list: GLAccountModel[]) => {
    // Filtriraj sve račune koji su djeca od odabranog roditelja

    let childEl = list.filter((l) => {
        // Preskoči roditelja
        if (l.account === rootEl) {
            return false;
        }
        // Provjeri ima li l.account iste početne znamenke kao rootEl i dulji je od roditelja
        if (l.account.startsWith(rootEl) && l.account.length === rootEl.length + 1) {
            return true;
        }
        return false;
    });

    if (childEl?.length === 0) {
        childEl = list.filter((gl) => {
            // Ako je roditelj uključen u broj računa i ima isti početak kao odabrani roditelj
            return gl?.account.startsWith(rootEl) && gl?.account !== rootEl && gl?.account.length > rootEl.length;
        });
    }
    return childEl;
};

export const findAllChildElements = (rootAccount: string, glAccountsList: GLAccountModel[]) => {
    const rootLength = rootAccount.length;
    const childElAcc: string[] = [];
    const childrenElements: GLAccountModel[] = [];
    for (const l of glAccountsList) {
        if (
            l.account !== rootAccount &&
            l.account.startsWith(rootAccount) &&
            l.account.length > rootLength &&
            !childElAcc?.includes(l.account)
        ) {
            childrenElements.push(l);
            childElAcc.push(l.account);
        }
    }

    return childrenElements;
};

export const findFirstOrLastGLAccountsChild = (
    rootAccount: string,
    glAccountsList: GLAccountModel[],
    lastChild?: boolean
) => {
    const rootLength = rootAccount.length;
    const childElAcc: string[] = [];
    const childrenElements: GLAccountModel[] = [];
    for (const l of glAccountsList) {
        if (
            l.account !== rootAccount &&
            l.account.startsWith(rootAccount) &&
            l.account.length > rootLength &&
            !childElAcc?.includes(l.account)
        ) {
            childrenElements.push(l);
            childElAcc.push(l.account);
        }
    }

    if (childrenElements.length <= 1) {
        return childrenElements;
    }

    const minLength = lastChild
        ? Math.max(...childrenElements.map((l: any) => l.account.length))
        : Math.min(...childrenElements.map((l: any) => l.account.length));
    const filteredElements = childrenElements.filter((l: any) => l.account.length === minLength);

    return filteredElements;
};

export const checkIfAccountHasParent = (account: string, _list: any[]) => {
    let closestRootElement = null;
    let firstRootElement = null;

    for (let i = 0; i < _list.length; i++) {
        const rootEl = _list[i].account;
        if (account.startsWith(rootEl)) {
            if (!firstRootElement) {
                firstRootElement = rootEl;
            }
            if (rootEl !== account && (!closestRootElement || rootEl.length > closestRootElement.length)) {
                closestRootElement = rootEl;
            }
        }
    }

    return { firstRootElement, closestRootElement };
};

export const getInputHeaderId = (SelectedHeadersEnums: StockHeaderEnums, input: string): number => {
    return Number(Object.keys(SelectedHeadersEnums).find((k: string) => SelectedHeadersEnums[Number(k)] === input));
};

export const pageChange = async (history: any, type: string, glDocumentId: number) => {
    if (glDocumentId) {
        const prevOrNextDoc = await getPrevOrNextGLDocument(type, glDocumentId);
        if (Number(prevOrNextDoc)) {
            history.push('/glDocuments/edit/' + prevOrNextDoc);
        }
    }
};

export const makeDataForTable = (glDocItems: GeneralLedgerExtended[], setData: Function) => {
    const _data: any = [];
    glDocItems.forEach((gl: GeneralLedgerExtended) => {
        let row = [];
        row.push(gl.id);
        row.push(gl.GLAccount?.account + ' - ' + gl.GLAccount?.name);
        row.push(gl?.Company?.name);
        row.push(formatNumber(gl.debt));
        row.push(formatNumber(gl.credit));
        row.push(formatNumber(gl.debtForeign ? gl.debtForeign : 0));
        row.push(formatNumber(gl.creditForeign ? gl.creditForeign : 0));
        row.push(gl.description);
        row.push(gl.account);
        row.push(gl?.companyId);
        _data.push(row);
    });
    setData([..._data]);
};

export const editOrCreateDataForAutoGLBooking = async (
    change: SheetChange,
    data: any,
    editedItem: GLAutoBookingModel
) => {
    let _editedItem = { ...editedItem },
        newData = [...data];

    return { _editedItem, newData };
};

export const returnEditedDataForAutoGLBooking = (
    editedItems: GLAutoBookingModel[],
    _editedItem: GLAutoBookingModel
): GLAutoBookingModel[] => {
    let _editedItems: GLAutoBookingModel[] = [...editedItems];
    if (_editedItems?.find((e) => e.id === _editedItem?.id)) {
        _editedItems = _editedItems.map((_d) => {
            if (_d?.id === _editedItem?.id) {
                return { ..._editedItem };
            } else {
                return { ..._d };
            }
        });
    } else {
        _editedItems.push(_editedItem);
    }

    return _editedItems;
};

type Choice = {
    label: string;
    value: string | number;
    type?: string;
};

export const getDocumentNameOptions = () => {
    const documentNameChoices: Choice[] = [];
    Object.keys(GLAutoBookingGoodsManagement)?.forEach((key: string) => {
        if (!Number(key)) {
            documentNameChoices.push({
                label: translate(key),
                value: key,
                type: 'goodsManagement',
            });
        }
    });

    Object.keys(GLAutoBookingRetailCalculation)?.forEach((key: string) =>
        documentNameChoices.push({
            label: translate(key),
            value: key,
            type: 'retailPriceCalculation',
        })
    );

    Object.keys(GLAutoBookingInvoices)?.forEach((key: string) =>
        documentNameChoices.push({
            label: translate(GLAutoBookingInvoices[key as keyof typeof GLAutoBookingInvoices]),
            value: GLAutoBookingInvoices[key as keyof typeof GLAutoBookingInvoices],
            type: 'invoices',
        })
    );

    Object.keys(GLAutoBookingInvoiceConclusion)?.forEach((key: string) =>
        documentNameChoices.push({
            label: translate(GLAutoBookingInvoiceConclusion[key as keyof typeof GLAutoBookingInvoiceConclusion]),
            value: GLAutoBookingInvoiceConclusion[key as keyof typeof GLAutoBookingInvoiceConclusion],
            type: 'invoiceConclusion',
        })
    );

    return documentNameChoices;
};

export const getDocumentItemOptions = (documentName: string) => {
    if (
        GLAutoBookingDocumentTypeNames[documentName as keyof typeof GLAutoBookingDocumentTypeNames] ===
        'goodsManagement'
    ) {
        return Object.keys(GLAutoBookingGoodsManagementValues).map((key: string) => {
            return {
                label: translate(key),
                value: key,
            };
        });
    } else if (
        GLAutoBookingDocumentTypeNames[documentName as keyof typeof GLAutoBookingDocumentTypeNames] ===
        'retailPriceCalculation'
    ) {
        return Object.keys(GLAutoBookingRetailCalculationValues).map((key: string) => {
            return {
                label: translate(key),
                value: key,
            };
        });
    } else if (
        GLAutoBookingDocumentTypeNames[documentName as keyof typeof GLAutoBookingDocumentTypeNames] === 'invoices'
    ) {
        return Object.keys(GLAutoBookingInvoiceValues).map((key: string) => {
            return {
                label: translate(key),
                value: key,
            };
        });
    } else if (
        GLAutoBookingDocumentTypeNames[documentName as keyof typeof GLAutoBookingDocumentTypeNames] ===
        'invoiceConclusion'
    ) {
        return Object.keys(GLAutoBookingInvoiceConclusionValues).map((key: string) => {
            return {
                label: translate(key),
                value: key,
            };
        });
    } else {
        return [];
    }
};

export const getClientTypeOptions = () => {
    const _options = [];
    for (const key in ClientTypeEnum) {
        if (!Number(key)) {
            _options.push({
                label: translate(key),
                value: ClientTypeEnum[key as keyof typeof ClientTypeEnum],
            });
        }
    }
    return _options;
};

export const getInvoiceTypeOptions = () => {
    const _options = [];
    for (const key in InvoiceType) {
        if (isNaN(Number(key))) {
            _options.push({
                label: translate(key),
                value: InvoiceType[key as keyof typeof InvoiceType],
            });
        }
    }
    return _options;
};

export const getPaymentTypeOptions = () => {
    const _options = [];
    for (const key in TypeOfPaymentEnums) {
        _options.push({
            value: key,
            label: translate(TypeOfPaymentEnums[key as keyof typeof TypeOfPaymentEnums]) + ' (' + key + ')',
        });
    }
    return _options;
};

export const getGLAccountOptions = (glAccounts: GLAccountModel[]) => {
    const _options = [];
    for (const key of glAccounts) {
        if (key.account) {
            _options.push({
                label: '(' + key.account + ') ' + key.name,
                value: key.account,
            });
        }
    }
    return _options;
};

export const updateRowData = (rowData: any, keyData: string, choice: any, rowIndex: number) => {
    let _rowData = { ...rowData };

    _rowData[keyData] = choice ? choice.value : null;

    _rowData.documentItem = keyData === 'documentName' ? null : _rowData.documentItem;
    _rowData.clientType =
        _rowData.documentName === GLAutoBookingDocumentNames.writeOffGoods ||
        _rowData.documentName === GLAutoBookingDocumentNames.internalGoodsConsumption ||
        _rowData.documentName === GLAutoBookingDocumentNames.correctionNote ||
        _rowData.documentName === GLAutoBookingDocumentNames.inventoryAdjustment ||
        _rowData.documentName === GLAutoBookingDocumentNames.interWarehouseTransfer ||
        _rowData.documentName === GLAutoBookingDocumentNames.productionNote ||
        _rowData.documentName === GLAutoBookingDocumentNames.stockReport
            ? null
            : _rowData.documentName === GLAutoBookingDocumentNames.regularInvoice ||
              _rowData.documentName === GLAutoBookingDocumentNames.advanceInvoice ||
              _rowData.documentName === GLAutoBookingDocumentNames.proformaInvoice ||
              _rowData.documentName === GLAutoBookingDocumentNames.proformaDispatchedInvoice ||
              _rowData.documentName === GLAutoBookingDocumentNames.goodsReceivingNote ||
              _rowData.documentName === GLAutoBookingDocumentNames.goodsDespatchNote ||
              _rowData.documentName === GLAutoBookingDocumentNames.offerNote ||
              _rowData.documentName === GLAutoBookingDocumentNames.procurementOrder ||
              _rowData.documentName === GLAutoBookingDocumentNames.retailPriceCalculation
            ? ClientTypeEnum.companyType
            : _rowData.clientType;

    _rowData.itemType =
        _rowData.documentName !== GLAutoBookingDocumentNames.regularInvoice &&
        _rowData.documentName !== GLAutoBookingDocumentNames.proformaInvoice &&
        _rowData.documentName !== GLAutoBookingDocumentNames.proformaDispatchedInvoice &&
        _rowData.documentName !== GLAutoBookingDocumentNames.invoiceConclusion
            ? null
            : _rowData.documentName === GLAutoBookingDocumentNames.invoiceConclusion &&
              (_rowData.documentItem === GLAutoBookingInvoiceConclusionValues.grossValue ||
                  _rowData.documentItem === GLAutoBookingInvoiceConclusionValues.grossValueWithDiscount ||
                  _rowData.documentItem === GLAutoBookingInvoiceConclusionValues.totalTaxValue)
            ? _rowData.itemType
            : _rowData.documentName === GLAutoBookingDocumentNames.invoiceConclusion &&
              (_rowData.documentItem === GLAutoBookingInvoiceConclusionValues.cashValue ||
                  _rowData.documentItem === GLAutoBookingInvoiceConclusionValues.cardValue ||
                  _rowData.documentItem === GLAutoBookingInvoiceConclusionValues.chequeValue ||
                  _rowData.documentItem === GLAutoBookingInvoiceConclusionValues.virmanValue)
            ? GLAutoBookingItemTypes.all
            : null;

    _rowData.itemSubtype =
        _rowData.documentName !== GLAutoBookingDocumentNames.regularInvoice &&
        _rowData.documentName !== GLAutoBookingDocumentNames.proformaInvoice &&
        _rowData.documentName !== GLAutoBookingDocumentNames.proformaDispatchedInvoice &&
        _rowData.documentName !== GLAutoBookingDocumentNames.invoiceConclusion &&
        _rowData.itemType !== GLAutoBookingItemTypes.posTerminal
            ? null
            : _rowData.documentName === GLAutoBookingDocumentNames.invoiceConclusion &&
              (_rowData.documentItem === GLAutoBookingInvoiceConclusionValues.grossValue ||
                  _rowData.documentItem === GLAutoBookingInvoiceConclusionValues.grossValueWithDiscount ||
                  _rowData.documentItem === GLAutoBookingInvoiceConclusionValues.totalTaxValue)
            ? _rowData.itemSubtype
            : null;

    _rowData.invoiceType =
        _rowData.documentName !== GLAutoBookingDocumentNames.regularInvoice &&
        _rowData.documentName !== GLAutoBookingDocumentNames.advanceInvoice &&
        _rowData.documentName !== GLAutoBookingDocumentNames.proformaInvoice &&
        _rowData.documentName !== GLAutoBookingDocumentNames.proformaDispatchedInvoice &&
        _rowData.documentName !== GLAutoBookingDocumentNames.invoiceConclusion
            ? null
            : _rowData.documentName === GLAutoBookingDocumentNames.regularInvoice
            ? InvoiceType.geniue
            : _rowData.documentName === GLAutoBookingDocumentNames.advanceInvoice
            ? InvoiceType.advance
            : _rowData.documentName === GLAutoBookingDocumentNames.proformaInvoice
            ? InvoiceType.proforma
            : _rowData.documentName === GLAutoBookingDocumentNames.proformaDispatchedInvoice
            ? InvoiceType.proformaDispatched
            : _rowData.invoiceType;

    _rowData.paymentType =
        _rowData.documentName !== GLAutoBookingDocumentNames.regularInvoice &&
        _rowData.documentName !== GLAutoBookingDocumentNames.advanceInvoice &&
        _rowData.documentName !== GLAutoBookingDocumentNames.proformaInvoice &&
        _rowData.documentName !== GLAutoBookingDocumentNames.proformaDispatchedInvoice &&
        _rowData.documentName !== GLAutoBookingDocumentNames.invoiceConclusion
            ? null
            : _rowData.documentName === GLAutoBookingDocumentNames.invoiceConclusion &&
              _rowData.documentItem === GLAutoBookingInvoiceConclusionValues.cashValue
            ? InvoiceTypeOfPayment.Cash
            : _rowData.documentName === GLAutoBookingDocumentNames.invoiceConclusion &&
              _rowData.documentItem === GLAutoBookingInvoiceConclusionValues.cardValue
            ? InvoiceTypeOfPayment.Card
            : _rowData.documentName === GLAutoBookingDocumentNames.invoiceConclusion &&
              _rowData.documentItem === GLAutoBookingInvoiceConclusionValues.chequeValue
            ? InvoiceTypeOfPayment.Cheque
            : _rowData.documentName === GLAutoBookingDocumentNames.invoiceConclusion &&
              _rowData.documentItem === GLAutoBookingInvoiceConclusionValues.virmanValue
            ? _rowData.paymentType
            : null;
    _rowData.inoCompany =
        _rowData.documentName === GLAutoBookingDocumentNames.writeOffGoods ||
        _rowData.documentName === GLAutoBookingDocumentNames.internalGoodsConsumption||
        _rowData.documentName === GLAutoBookingDocumentNames.correctionNote ||
        _rowData.documentName === GLAutoBookingDocumentNames.inventoryAdjustment ||
        _rowData.documentName === GLAutoBookingDocumentNames.interWarehouseTransfer ||
        _rowData.documentName === GLAutoBookingDocumentNames.productionNote ||
        _rowData.documentName === GLAutoBookingDocumentNames.invoiceConclusion
            ? false
            : _rowData.inoCompany;

    _rowData.taxFree =
        _rowData.documentName === GLAutoBookingDocumentNames.writeOffGoods ||
        _rowData.documentName === GLAutoBookingDocumentNames.internalGoodsConsumption||
        _rowData.documentName === GLAutoBookingDocumentNames.correctionNote ||
        _rowData.documentName === GLAutoBookingDocumentNames.inventoryAdjustment ||
        _rowData.documentName === GLAutoBookingDocumentNames.interWarehouseTransfer ||
        _rowData.documentName === GLAutoBookingDocumentNames.productionNote ||
        _rowData.documentName === GLAutoBookingDocumentNames.invoiceConclusion
            ? false
            : _rowData.taxFree;

    _rowData.rowIndex = rowIndex;
    console.log('_rowData====================================', _rowData);
    return _rowData;
};

export const validateAutoBookingRules = (
    AutoBookingRules: AutoBookingRulesInterface,
    data: any,
    ruleKey: string | null
): Record<string, boolean> => {
    const errors: Record<string, boolean> = {};

    if (!ruleKey) {
        errors.documentName = true;
        return errors;
    }

    const rule: AutoBookingRule = AutoBookingRules[ruleKey];

    if (!data.documentName) {
        errors.documentName = true;
    }
    if (!data.documentItem) {
        errors.documentItem = true;
    }
    if (!data.debtGLAccount) {
        errors.debtGLAccount = true;
    }
    if (!data.creditGLAccount) {
        errors.creditGLAccount = true;
    }

    // Provjeri documentItem
    if (rule.documentItem && data.documentItem && !rule.documentItem.includes(data.documentItem)) {
        errors.documentItem = true;
    }

    // Provjeri clientType
    if (rule.clientType && rule.clientType.length > 0 && !rule.clientType.includes(data.clientType)) {
        errors.clientType = true;
    }

    // Provjeri invoiceType
    if (rule.invoiceType && rule.invoiceType.length > 0 && !rule.invoiceType.includes(data.invoiceType)) {
        errors.invoiceType = true;
    }

    // Provjeri paymentType
    if (rule.paymentType && data.paymentType) {
        const validPaymentType = rule.paymentType(data.documentName, data.documentItem).includes(data.paymentType);
        if (!validPaymentType) {
            errors.paymentType = true;
        }
    }

    return errors;
};

// Funkcija koja vraća odgovarajući tip fakture na temelju dokumenta
export const getInvoiceType = (documentName: string) => {
    switch (documentName) {
        case GLAutoBookingDocumentNames.regularInvoice:
            return InvoiceType.geniue;
        case GLAutoBookingDocumentNames.advanceInvoice:
            return InvoiceType.advance;
        case GLAutoBookingDocumentNames.proformaInvoice:
            return InvoiceType.proforma;
        case GLAutoBookingDocumentNames.proformaDispatchedInvoice:
            return InvoiceType.proformaDispatched;
        default:
            return null;
    }
};

// Funkcija koja vraća odgovarajući tip plačanja
export const getPaymentType = (documentName: string, documentItem: string) => {
    if (documentName !== GLAutoBookingDocumentNames.invoiceConclusion) return null;

    switch (documentItem) {
        case GLAutoBookingInvoiceConclusionValues.cashValue:
            return InvoiceTypeOfPayment.Cash;
        case GLAutoBookingInvoiceConclusionValues.cardValue:
            return InvoiceTypeOfPayment.Card;
        case GLAutoBookingInvoiceConclusionValues.chequeValue:
            return InvoiceTypeOfPayment.Cheque;
        default:
            return null;
    }
};

export const getChoices = (
    autoBookingRules: AutoBookingRulesInterface,
    documentName: string,
    columKey: string,
    documentItem?: string,
    itemType?: string
) => {
    const choices: Choice[] = [];
    const autoBookRules: AutoBookingRule = autoBookingRules[documentName];
    if (autoBookRules) {
        switch (columKey) {
            case 'documentItem':
                const _docItemRules: string[] = autoBookRules.documentItem;

                for (const key of _docItemRules) {
                    const obj: Choice = {
                        label: translate(key),
                        value: key,
                    };
                    choices.push(obj);
                }
                return choices;

            case 'itemType':
                let _itemTypeRules: string[] = [];
                if (
                    (documentName === GLAutoBookingDocumentNames.invoiceConclusion &&
                        (documentItem === GLAutoBookingInvoiceConclusionValues.grossValue ||
                            documentItem === GLAutoBookingInvoiceConclusionValues.grossValueWithDiscount ||
                            documentItem === GLAutoBookingInvoiceConclusionValues.totalTaxValue)) ||
                    documentName === GLAutoBookingDocumentNames.regularInvoice ||
                    documentName === GLAutoBookingDocumentNames.advanceInvoice ||
                    documentName === GLAutoBookingDocumentNames.proformaInvoice ||
                    documentName === GLAutoBookingDocumentNames.proformaDispatchedInvoice
                ) {
                    _itemTypeRules = Object.keys(GLAutoBookingItemTypes);
                }else if (
                    documentName === GLAutoBookingDocumentNames.invoiceConclusion &&
                    (documentItem === GLAutoBookingInvoiceConclusionValues.cashValue ||
                        documentItem === GLAutoBookingInvoiceConclusionValues.cardValue ||
                        documentItem === GLAutoBookingInvoiceConclusionValues.chequeValue ||
                        documentItem === GLAutoBookingInvoiceConclusionValues.virmanValue)
                ) {
                    _itemTypeRules = [GLAutoBookingItemTypes.all];
                }

                if (_itemTypeRules) {
                    for (const key of _itemTypeRules) {
                        const obj: Choice = {
                            label: translate(
                                GLAutoBookingItemTypesTranslate[key as keyof typeof GLAutoBookingItemTypesTranslate]
                            ),
                            value: key,
                        };
                        choices.push(obj);
                    }
                    return choices;
                } else {
                    return choices;
                }

            case 'itemSubtype':
                let _itemSubtypeRules: string[] = [];
                if (itemType === GLAutoBookingItemTypes.posTerminal) {
                    _itemSubtypeRules = Object.keys(PosItemSubTypesTranslate);
                }

                if (_itemSubtypeRules) {
                    for (const key of _itemSubtypeRules) {
                        const obj: Choice = {
                            label: translate(PosItemSubTypesTranslate[key as keyof typeof PosItemSubTypesTranslate]),
                            value: key,
                        };
                        choices.push(obj);
                    }
                    return choices;
                } else {
                    return choices;
                }

            case 'clientType':
                const _clientTypeRules: number[] | null = autoBookRules.clientType;

                if (_clientTypeRules) {
                    for (const key of _clientTypeRules) {
                        const obj: Choice = {
                            label: translate(ClientTypeEnum[key]),
                            value: key,
                        };
                        choices.push(obj);
                    }
                    return choices;
                } else {
                    return choices;
                }

            case 'invoiceType':
                const _invTypeRules: number[] | null = autoBookRules.invoiceType;

                if (_invTypeRules) {
                    for (const key of _invTypeRules) {
                        const obj: Choice = {
                            label: translate(InvoiceType[key]),
                            value: key,
                        };
                        choices.push(obj);
                    }
                    return choices;
                } else {
                    return choices;
                }

            case 'paymentType':
                let _payTypeRules: string[] = [];
                if (documentName === 'invoiceConclusion') {
                    const paymentMapping: any = {
                        cashValue: ['G'],
                        cardValue: ['K'],
                        chequeValue: ['C'],
                        virmanValue: ['V', 'T'],
                        grossValue: [],
                        baseValue: [],
                        taxValue: [],
                        totalTaxValue: [],
                    };
                    _payTypeRules = documentItem ? paymentMapping[documentItem] || [] : [];
                } else {
                    _payTypeRules = Object.keys(TypeOfPaymentEnums);
                }

                if (_payTypeRules) {
                    for (const key of _payTypeRules) {
                        const obj: Choice = {
                            label: key + ' - ' + translate(TypeOfPaymentEnums[key as keyof typeof TypeOfPaymentEnums]),
                            value: key,
                        };
                        choices.push(obj);
                    }
                    return choices;
                } else {
                    return choices;
                }

            case 'glTypeId':
                const _glTypeRules: TypeAndClassModel[] = autoBookRules.glTypeId;

                for (const key of _glTypeRules) {
                    if (key?.id) {
                        const obj: Choice = {
                            label: translate(key?.name),
                            value: key?.id,
                        };
                        choices.push(obj);
                    }
                }
                return choices;

            case 'glClassId':
                const _glClassRules: TypeAndClassModel[] = autoBookRules.glClassId;

                for (const key of _glClassRules) {
                    if (key?.id) {
                        const obj: Choice = {
                            label: translate(key?.name),
                            value: key?.id,
                        };
                        choices.push(obj);
                    }
                }
                return choices;

            case 'creditGLAccount':
                const _creditGLAccountRules: GLAccountModel[] = autoBookRules.creditGLAccount;

                for (const key of _creditGLAccountRules) {
                    if (key?.account) {
                        const obj: Choice = {
                            label: key.account + ' - ' + key?.name,
                            value: key?.account,
                        };
                        choices.push(obj);
                    }
                }
                return choices;

            case 'debtGLAccount':
                const _debtGLAccountRules: GLAccountModel[] = autoBookRules.debtGLAccount;

                for (const key of _debtGLAccountRules) {
                    if (key?.account) {
                        const obj: Choice = {
                            label: key.account + ' - ' + key?.name,
                            value: key?.account,
                        };
                        choices.push(obj);
                    }
                }
                return choices;
            default:
                return choices;
        }
    } else {
        return choices;
    }
};

export const GLDocumentTemplate: GlDocumentWithItems = {
    docNumber: '',
    name: '',
    type: null,
    class: null,
    postingDate: new Date().getTime(),
    issueDate: null,
    dueDate: null,
    userUuid: '',
    isLocked: false,
    isDeleted: false,
    companyId: null,
    credit: 0,
    debt: 0,
    GeneralLedger: [],
};

export const GLDocumentHeaderEnums: StockHeaderEnums = {
    0: 'id',
    1: 'account',
    2: '_company',
    3: 'debt',
    4: 'credit',
    5: 'debtForeign',
    6: 'creditForeign',
    7: 'description',
    8: 'glAccountFK',
    9: 'companyId',
};

export const getPaddingsForLineDebth = (lineDebth: number) => {
    const paddingForLine: any = {
        1: '8px',
        2: '16px',
        3: '24px',
        4: '32px',
        5: '40px',
        6: '48px',
        7: '56px',
        8: '64px',
        9: '72px',
        10: '80px',
    };

    return paddingForLine[lineDebth];
};

export const HeadersEnums: StockHeaderEnums = {
    0: 'id',
    1: 'account',
    2: 'debt',
    3: 'credit',
};

export const GLAutoBookingHeaderEnums: StockHeaderEnums = {
    0: 'id',
    1: 'documentName',
    2: 'documentItem',
    3: '_description',
    4: 'clientType',
    5: 'invoiceType',
    6: 'paymentType',
    7: 'inoCompany',
    8: 'taxFree',
    9: 'glTypeId',
    10: 'glClassId',
    11: 'creditGLAccount',
    12: 'debtGLAccount',
    13: 'isBeingUsedFlag',
};

export const GLAutoBookingHeaderEnumsCellWidth: StockHeaderEnums = {
    0: '50px',
    1: '120px',
    2: '120px',
    3: '120px',
    4: '100px',
    5: '100px',
    6: '100px',
    7: '80px',
    8: '80px',
    9: '100px',
    10: '100px',
    11: '100px',
    12: '100px',
    13: '100px',
};

export const defaultEmptyRow: GLAutoBookingModel = {
    id: null,
    documentName: '',
    documentItem: '',
    itemType: null,
    itemSubtype: null,
    description: null,
    clientType: null,
    invoiceType: null,
    paymentType: null,
    inoCompany: false,
    taxFree: false,
    glTypeId: null,
    glClassId: null,
    creditGLAccount: '',
    debtGLAccount: '',
    automaticGLBooking: false,
    isBeingUsedFlag: false,
};

export const DocumentAutoGLBookingHeaderEnums: StockHeaderEnums = {
    0: 'id',
    1: 'documentName',
    2: 'documentItem',
    3: 'description',
    4: 'glType',
    5: 'glClass',
    6: 'creditGLAccount',
    7: 'debtGLAccount',
    8: 'isBeingUsedFlag',
};

export const customStyles = {
    control: (provided: any, state: any) => ({
        ...provided,
        background: state.isDisabled ? '#f3f6fb' : '#fff',
        borderColor: '#D8E0EC',
        minHeight: '32px',
        // height: '32px',
        minWidth: '500px',
        boxShadow: state.isFocused ? null : null,
        fontFamily: 'Work Sans',
        fontStyle: 'normal',
        fontWeight: '400',
        fontSize: '12px',
        lineHeight: '16px',
    }),

    valueContainer: (provided: any, state: any) => ({
        ...provided,
        // height: '32px',
        padding: '0 6px',
    }),

    input: (provided: any, state: any) => ({
        ...provided,
        margin: '0px',
    }),
    indicatorSeparator: (state: any) => ({
        display: 'none',
    }),
    indicatorsContainer: (provided: any, state: any) => ({
        ...provided,
        // height: '32px',
    }),
    menu: (base: any) => ({ ...base, zIndex: 9999 }),
};
